import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

const ConsultingProcess = () => {
  const navigate = useNavigate();
  const isMobile = window.innerWidth < 768;

  const [leftRef, leftInView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  const handleExploreClick = () => {
    navigate('/solution/cloud-telecom');
  };
  const [rightRef, rightInView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const styles = {
    container: {
      padding: isMobile ? '20px' : '40px',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif'
    },
    mainLayout: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      gap: '40px'
    },
    leftSection: {
      width: '100%',
      flex: isMobile ? 'auto' : '0 0 40%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: isMobile ? 'center' : 'flex-start', // Center only on mobile
    },
    rightSection: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '60px',
      position: 'relative',
      paddingLeft: '45px',
      flex: isMobile ? 'auto' : '0 0 55%'
    },
    header: {
      fontSize: 'clamp(24px, 5vw, 38px)',
      fontWeight: 'bold',
      color: '#400684',
      fontFamily: 'Oswald, sans-serif',
      textAlign: isMobile ? 'center' : 'left', // Center only on mobile
      width: '100%'
    },
    description: {
      color: '#666',
      lineHeight: '1.8',
      fontSize: 'clamp(14px, 2vw, 16px)',
      marginTop: '30px',
      textAlign: isMobile ? 'center' : 'left', // Center only on mobile
      width: '100%'
    },
    card: {
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '8px',
      color: 'black',
      position: 'relative',
      zIndex: '2',
      width: '100%',
      minHeight: '120px',
      display: 'flex',
      marginBottom: '20px',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: isMobile ? 'center' : 'flex-start', // Center only on mobile
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)'
    },
    cardNumber: {
      position: 'absolute',
      left: '-45px',
      top: '20px',
      width: '30px',
      height: '30px',
      backgroundColor: '#400684',
      color: 'white',
      fontSize: '14px',
      fontWeight: 'bold',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '3',
      border: '2px solid white'
    },
    cardTitle: {
      fontSize: 'clamp(16px, 2.5vw, 18px)',
      fontWeight: 'bold',
      marginBottom: '8px',
      color: '#400684',
      textAlign: isMobile ? 'center' : 'left', // Center only on mobile
      width: '100%'
    },
    cardDescription: {
      color: 'black',
      fontSize: 'clamp(13px, 1.8vw, 14px)',
      lineHeight: '1.4',
      textAlign: isMobile ? 'center' : 'left', // Center only on mobile
      width: '100%'
    },
    dottedLine: {
      position: 'absolute',
      left: '15px',
      top: '0',
      bottom: '0',
      width: '2px',
      background: 'linear-gradient(to bottom, #8b5cf6 50%, transparent 50%)',
      backgroundSize: '10px 15px',
      zIndex: '1'
    },
    buttonContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: isMobile ? 'center' : 'flex-start', // Center only on mobile
      marginTop: '20px'
    },
    learnMoreButton: {
      backgroundColor: '#400684',
      color: 'white',
      padding: '12px 24px',
      border: 'none',
      borderRadius: '4px',
      fontSize: 'clamp(14px, 2vw, 16px)',
      fontFamily: 'Oswald, sans-serif',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      ':hover': {
        backgroundColor: '#2d0461'
      }
    }
  };

  // Animation variants remain the same
  const leftVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  const rightVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  React.useEffect(() => {
    const handleResize = () => {
      window.location.reload();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.mainLayout}>
        <motion.div
          ref={leftRef}
          initial="hidden"
          animate={leftInView ? "visible" : "hidden"}
          variants={leftVariants}
          style={styles.leftSection}
        >
          <h1 style={styles.header}>
            INNOVATIVE TECHONOLOGY SOLUTIONS
          </h1>
          <p style={styles.description}>
            Whether you are looking to monitor and connect your business branches, 
            hoping to obtain high-speed DIA internet from your service provider, 
            are dreaming of an innovative, agile, cost-efficient solution using our unique IoT, 
            or are looking for excellent business voice services and bundles. 
            we are here to serve you with tailored solutions and packages.
          </p>
          <div style={styles.buttonContainer}>
            <motion.button
              style={styles.learnMoreButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleExploreClick}
            >
              Explore Solutions
            </motion.button>
          </div>
        </motion.div>

        <motion.div
          ref={rightRef}
          initial="hidden"
          animate={rightInView ? "visible" : "hidden"}
          variants={rightVariants}
          style={styles.rightSection}
        >
          <div style={styles.dottedLine}></div>
          
          <motion.div
            variants={cardVariants}
            style={styles.card}
          >
            <div style={styles.cardNumber}>01</div>
            <h3 style={styles.cardTitle}>NETWORK MONITORING SOLUTIONS</h3>
            <p style={styles.cardDescription}>
              Comprehensive solutions for monitoring and connecting multiple business 
              branches with advanced network tools.
            </p>
          </motion.div>

          <motion.div
            variants={cardVariants}
            style={styles.card}
          >
            <div style={styles.cardNumber}>02</div>
            <h3 style={styles.cardTitle}>IOT INTEGRATION & VOICE SERVICES</h3>
            <p style={styles.cardDescription}>
              Innovative IoT solutions combined with premium business voice services 
              for your organization.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ConsultingProcess;